import { useEffect, useRef } from 'react';
import type { DataSchema, Schema } from 'types';
import type { TransformedDataType } from 'utils/agendaRecord/agendaRecord';
import { getSelectRowId } from 'utils/table';

const useAutoSelectResultModal = ({
  isModal,
  isHeliosTable,
  transformedData,
  isAgendaViewFetching,
  isAutoSelectSingleResult,
  onAutoSelectSingleResult,
}: {
  isModal: boolean;
  isHeliosTable: boolean;
  transformedData: TransformedDataType[];
  isAgendaViewFetching: boolean;
  isAutoSelectSingleResult?: boolean;
  onAutoSelectSingleResult?: (rowId: string, row: Schema<string | number, DataSchema>) => void;
}) => {
  const autoSelectedRowIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Auto select is need only for modal view and when the auto select feature is enabled.
    if (!isModal || !isAutoSelectSingleResult) {
      autoSelectedRowIdRef.current = null;
      return;
    }

    // Prevent auto select of old data when modal is reopened
    if (isAgendaViewFetching || transformedData.length !== 1) {
      return;
    }

    const row = transformedData[0];
    const rowId = getSelectRowId(row, isHeliosTable);

    // Skip auto select if the same row was already auto selected (for example user wants to manually reselect row).
    if (autoSelectedRowIdRef.current === rowId) {
      return;
    }

    autoSelectedRowIdRef.current = rowId;
    onAutoSelectSingleResult?.(rowId, row as Schema<string | number, DataSchema>);
  }, [isAgendaViewFetching, isAutoSelectSingleResult, isHeliosTable, isModal, onAutoSelectSingleResult, transformedData]);
};

export { useAutoSelectResultModal };
